import Constants from 'api-constants'
import { NagraTypes } from '../models/NagraTypes'
import { EPL_GAME_RELATED_TYPE } from '../ProgramApi/helpers'

/**
 * Gets proper content type relates to programType
 * @param {Constants.programType} programType - one of program types from {@link Constants}
 * @returns {NagraTypes.CONTENT_TYPE}
 */
export const getNagraContentType = (programType) => {
    switch (programType) {
        case Constants.programType.live:
        case Constants.programType.channel:
            return NagraTypes.CONTENT_TYPE.BTV
        case Constants.programType.vod:
        case Constants.programType.movie:
        case Constants.programType.episode:
        case Constants.programType.orphanEpisode:
        case Constants.programType.vodCatchup:
            return NagraTypes.CONTENT_TYPE.VOD
        case Constants.programType.series:
        case Constants.programType.collection:
        case Constants.programType.episodicShow:
            return NagraTypes.CONTENT_TYPE.SERIES
        case Constants.programType.season:
        case Constants.programType.orphanSeason:
            return NagraTypes.CONTENT_TYPE.SEASON
        case Constants.programType.dvr:
        case Constants.programType.replay:
        case Constants.programType.unknown:
        default:
            return undefined
    }
}

/**
 * Gets proper recommendation type relates to programType
 * @param {Constants.programType} relatedType - one of program types from {@link Constants}
 * @returns {NagraTypes.RECOMMENDATION_TYPE}
 */
export const getNagraRecommendationType = (relatedType) => {
    switch (relatedType) {
        case EPL_GAME_RELATED_TYPE:
            return NagraTypes.RECOMMENDATION_TYPE.EPL
        case Constants.programType.live:
        case Constants.programType.channel:
            return NagraTypes.RECOMMENDATION_TYPE.BTV
        case Constants.programType.vod:
        case Constants.programType.movie:
        case Constants.programType.season:
        case Constants.programType.series:
        case Constants.programType.episode:
        case Constants.programType.collection:
        case Constants.programType.episodicShow:
        case Constants.programType.orphanSeason:
        case Constants.programType.orphanEpisode:
        case Constants.programType.vodCatchup:
            return NagraTypes.RECOMMENDATION_TYPE.VOD
        case Constants.programType.dvr:
        case Constants.programType.replay:
        case Constants.programType.unknown:
        default:
            return NagraTypes.RECOMMENDATION_TYPE.UNKNOWN
    }
}

/**
 * Gets proper OS (Operating System) name due to Nagra's specification
 * @param {String} platform - name of platform
 * @returns {String} returns proper OS name for Nagra's backend
 */
export const getNagraDeviceType = (platform) => {
    switch (platform) {
        case Constants.platform.androidTvStb:
        case Constants.platform.androidTv:
        case Constants.platform.amazonTv:
            return 'ATV'

        case Constants.platform.android:
            return 'Android'

        case Constants.platform.chromecast:
            return 'Android'

        case Constants.platform.ios:
        case Constants.platform.tvos:
        case Constants.platform.safari:
            return 'IOS'

        default:
            return 'Chrome'
    }
}

/**
 * Gets Hardware Type
 * @param {String} platform - name of platform
 * @returns {String} returns Nagra Hardware type
 */
export const getNagraHardwareType = (platform) => {
    switch (platform) {
        case Constants.platform.androidTvStb:
        case Constants.platform.androidTv:
            return {
                HardwareOS: NagraTypes.HARDWARE_OS.ANDROID,
                HardwareType: NagraTypes.HARDWARE_TYPE.STB,
            }

        case Constants.platform.amazonTv:
            return {
                HardwareOS: NagraTypes.HARDWARE_OS.ANDROID,
                HardwareType: NagraTypes.HARDWARE_TYPE.AMAZON_TV,
            }

        case Constants.platform.android:
            return {
                HardwareOS: NagraTypes.HARDWARE_OS.ANDROID,
                HardwareType: NagraTypes.HARDWARE_TYPE.MOBILE,
            }

        case Constants.platform.ios:
            return {
                HardwareOS: NagraTypes.HARDWARE_OS.IOS,
                HardwareType: NagraTypes.HARDWARE_TYPE.MOBILE,
            }

        case Constants.platform.chromecast:
            return {
                HardwareOS: NagraTypes.HARDWARE_OS.ANDROID,
                HardwareType: NagraTypes.HARDWARE_TYPE.CHROMECAST,
            }

        case Constants.platform.tvos:
            return {
                HardwareOS: NagraTypes.HARDWARE_OS.IOS,
                HardwareType: NagraTypes.HARDWARE_TYPE.APPLE_TV,
            }

        case Constants.platform.xbox:
            return {
                HardwareOS: NagraTypes.HARDWARE_OS.WINDOWS,
                HardwareType: NagraTypes.HARDWARE_TYPE.XBOX,
            }

        case Constants.platform.playstation:
            return {
                HardwareOS: NagraTypes.HARDWARE_OS.LINUX,
                HardwareType: NagraTypes.HARDWARE_TYPE.PLAYSTATION,
            }

        case Constants.platform.edgeChromium:
        case Constants.platform.safari:
        case Constants.platform.chrome:
        case Constants.platform.firefox:
            return {
                HardwareOS: NagraTypes.HARDWARE_OS.COMPUTER,
                HardwareType: NagraTypes.HARDWARE_TYPE.BROWSER,
            }
        case Constants.platform.smartTvOs:
            return {
                HardwareOS: NagraTypes.HARDWARE_OS.COMPUTER,
                HardwareType: NagraTypes.HARDWARE_TYPE.SMART_TV,
            }

        default:
            return {
                HardwareOS: NagraTypes.HARDWARE_OS.COMPUTER,
                HardwareType: NagraTypes.HARDWARE_TYPE.BROWSER,
            }
    }
}

/**
 * Gets Hardware Manufacturer/Model
 * @param {String} platform - name of platform
 * @returns {String} returns Nagra Hardware type
 */
export const getHardware = (deviceData = {}, platform = Constants.platform.androidTvStb) => {
    const { manufacturer, brand, model, deviceNickName } = deviceData

    let devicePlatform
    switch (platform) {
        case Constants.platform.androidTvStb:
            devicePlatform = 'STB'
            break
        case Constants.platform.androidTv:
            devicePlatform = 'Android TV'
            break
        case Constants.platform.amazonTv:
            devicePlatform = 'Amazon TV'
            break
        case Constants.platform.android:
            devicePlatform = 'Android'
            break
        case Constants.platform.chromecast:
            devicePlatform = 'Chromecast'
            break
        case Constants.platform.ios:
            devicePlatform = 'iPhone'
            break
        case Constants.platform.tvos:
            devicePlatform = 'Apple TV'
            break
        case Constants.platform.chrome:
            devicePlatform = 'Chrome'
            break
        case Constants.platform.firefox:
            devicePlatform = 'Firefox'
            break
        case Constants.platform.edge:
            devicePlatform = 'Edge'
            break
        case Constants.platform.edgeChromium:
            devicePlatform = 'Edge Chromium'
            break
        case Constants.platform.safari:
            devicePlatform = 'Safari'
            break
        default:
            devicePlatform = platform
            break
    }

    let deviceModel
    if (model) {
        deviceModel = model
    } else if (brand) {
        deviceModel = `${brand} ${devicePlatform} `
    } else if (deviceNickName) {
        deviceModel = deviceNickName
    } else if (devicePlatform) {
        deviceModel = devicePlatform
    }

    return {
        model: deviceModel || 'unknown',
        manufacturer: manufacturer || 'unknown',
    }
}

/**
 * Returns VODs filtered by episodes or non episodes.
 *
 * The UI does not manage properly VOD swimlanes composed by VOD Movies/Series and episodes.
 * if there are more episodes in the answer return episodes and filter other type of contents,
 * but if there are less episodes episodes will be filtered out.
 * @param {Observable<Array<Season|Episode|Movie|Live>>} programs List of VODs from the proxy
 * @returns {Observable<Array<Season|Movie|Live>>|Observable<Array<Episode>>} Filtered list of VODs with only episodes
 * or only non episodes
 */
export const getVodsFilteredByType = (programs) => {
    const programsMapping = programs.reduce(
        (acc, program) => {
            if (
                (program.isEpisode && program.isEpisode()) ||
                (program.isOrphanEpisode && program.isOrphanEpisode())
            ) {
                acc.episodes.push(program)
            } else {
                acc.nonEpisodes.push(program)
            }

            return acc
        },
        { episodes: [], nonEpisodes: [] }
    )
    return programsMapping.episodes.length > programsMapping.nonEpisodes.length
        ? programsMapping.episodes
        : programsMapping.nonEpisodes
}

/**
 * Add image resizing attributes
 * @param {String} picture
 * @param {'thumbnail'|'backdground'} imageType
 * @returns
 */
export const addImageResizingAttributes = (picture, imageType = 'thumbnail') => {
    switch (imageType) {
        case 'thumbnail':
            return picture ? `${picture}?w=341&h=192` : undefined

        case 'backdrop':
        case 'backdground':
            return picture ? `${picture}?w=960&h=540` : undefined

        default:
            return picture
    }
}

export const resolveBookmarkType = (bookmarkType) => {
    switch (bookmarkType) {
        case Constants.programType.vod:
        case Constants.programType.movie:
        case Constants.programType.season:
        case Constants.programType.episode:
        case Constants.programType.orphanEpisode:
        case Constants.programType.vodCatchup:
            return Constants.programType.vod

        case Constants.programType.series:
            return Constants.programType.series

        case Constants.programType.live:
        case Constants.programType.replay:
            return Constants.programType.live

        case Constants.programType.dvr:
            return Constants.programType.dvr
        default:
            return undefined
    }
}
